import apiClient from './apiClient'

export type DocType = 1 | 2 | 3  // 1=Signature, 2=Acknowledgment, 3=LmsTraining

export const DocTypeLabels: Record<DocType, string> = {
  1: 'Podpis',
  2: 'Seznámení',
  3: 'Školení LMS',
}

export interface DocumentDefinitionListItem {
  id: number
  name: string
  documentType: DocType
  documentUrl: string | null
  note: string | null
  validFrom: string | null
  validTo: string | null
  assignOnHire: boolean
  assignOnHireContractor: boolean
  deadlineDate: string | null
  deadlineDays: number | null
  reminderDaysBefore: number | null
  isActive: boolean
  pairingCode: string | null
  totalAssigned: number
  totalCompleted: number
}

export interface DocumentDefinitionDetail extends DocumentDefinitionListItem {
  assignments: PersonDocumentRecord[]
}

export interface PersonDocumentRecord {
  id: number
  documentDefinitionId: number
  documentName: string
  documentType: DocType
  documentUrl: string | null
  isCompleted: boolean
  completedAt: string | null
  completedBy: string | null
  employeeNote: string | null
  deadlineDate: string | null
  // Přiřazená osoba
  employeeId: number | null
  contractorId: number | null
  isContractor: boolean
  personFullName: string
  personEmail: string | null
  validFrom: string | null
  validTo: string | null
}

/** @deprecated Použij PersonDocumentRecord */
export type EmployeeDocumentRecord = PersonDocumentRecord

export interface SaveDocumentDefinitionRequest {
  name: string
  documentType: number
  documentUrl?: string | null
  note?: string | null
  validFrom?: string | null
  validTo?: string | null
  assignOnHire: boolean
  assignOnHireContractor: boolean
  deadlineDate?: string | null
  deadlineDays?: number | null
  reminderDaysBefore?: number | null
  isActive: boolean
  pairingCode?: string | null
}

export interface ReminderEmailDto {
  subject: string
  body: string
  recipients: string[]
  totalPending: number
}

export const documentDefinitionsService = {
  getAll: (includeInactive = false) =>
    apiClient.get<DocumentDefinitionListItem[]>('/document-definitions', { params: { includeInactive } }).then(r => r.data),

  getById: (id: number) =>
    apiClient.get<DocumentDefinitionDetail>(`/document-definitions/${id}`).then(r => r.data),

  create: (req: SaveDocumentDefinitionRequest) =>
    apiClient.post<{ id: number }>('/document-definitions', req).then(r => r.data),

  update: (id: number, req: SaveDocumentDefinitionRequest) =>
    apiClient.put(`/document-definitions/${id}`, req),

  delete: (id: number) =>
    apiClient.delete(`/document-definitions/${id}`),

  permanentDelete: (id: number) =>
    apiClient.delete(`/document-definitions/${id}/permanent`),

  assign: (id: number, employeeIds: number[]) =>
    apiClient.post<{ assigned: number }>(`/document-definitions/${id}/assign`, { employeeIds }).then(r => r.data),

  assignAllEmployees: (id: number) =>
    apiClient.post<{ assigned: number }>(`/document-definitions/${id}/assign-all-employees`).then(r => r.data),

  assignAllContractors: (id: number) =>
    apiClient.post<{ assigned: number }>(`/document-definitions/${id}/assign-all-contractors`).then(r => r.data),

  assignContractors: (id: number, contractorIds: number[]) =>
    apiClient.post<{ assigned: number }>(`/document-definitions/${id}/assign-contractors`, { contractorIds }).then(r => r.data),

  unassignPersonDocument: (personDocumentId: number) =>
    apiClient.delete(`/document-definitions/person-documents/${personDocumentId}`),

  getReminderEmail: (id: number) =>
    apiClient.get<ReminderEmailDto>(`/document-definitions/${id}/reminder-email`).then(r => r.data),

  getNotificationEmail: (id: number) =>
    apiClient.get<ReminderEmailDto>(`/document-definitions/${id}/notification-email`).then(r => r.data),

  sendReminder: (id: number, subject: string, body: string): Promise<{ sent: number }> =>
    apiClient.post(`/document-definitions/${id}/send-reminder`, { subject, body }).then((r: { data: { sent: number } }) => r.data),

  sendNotification: (id: number, subject: string, body: string): Promise<{ sent: number }> =>
    apiClient.post(`/document-definitions/${id}/send-notification`, { subject, body }).then((r: { data: { sent: number } }) => r.data),

  getEmployeeDocuments: (employeeId: number) =>
    apiClient.get<PersonDocumentRecord[]>(`/document-definitions/employee/${employeeId}`).then(r => r.data),

  getContractorDocuments: (contractorId: number) =>
    apiClient.get<PersonDocumentRecord[]>(`/document-definitions/contractor/${contractorId}`).then(r => r.data),

  completeDocument: (personDocumentId: number, employeeNote?: string, completedAt?: string) =>
    apiClient.patch(`/document-definitions/person-documents/${personDocumentId}/complete`, {
      employeeNote,
      completedAt: completedAt ? new Date(completedAt).toISOString() : undefined,
    }),

  updateCompletionDate: (personDocumentId: number, completedAt: string) =>
    apiClient.patch(`/document-definitions/person-documents/${personDocumentId}/completion-date`, {
      completedAt: new Date(completedAt).toISOString(),
    }),

  uncompleteDocument: (personDocumentId: number) =>
    apiClient.patch(`/document-definitions/person-documents/${personDocumentId}/uncomplete`, {}),

  updateNote: (personDocumentId: number, note: string | null) =>
    apiClient.patch(`/document-definitions/person-documents/${personDocumentId}/note`, { note }),
}
